import { useCallback, useEffect, useMemo, useState } from "react";
import { API_URL, authFetch } from "../../utils/auth";
import "../../../css/partner/Meetings.css";

const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const timeZones = [
  ["America/New_York", "Eastern Time - America/New_York"],
  ["America/Chicago", "Central Time - America/Chicago"],
  ["America/Denver", "Mountain Time - America/Denver"],
  ["America/Los_Angeles", "Pacific Time - America/Los_Angeles"],
  ["America/Phoenix", "Arizona Time - America/Phoenix"],
  ["America/Anchorage", "Alaska Time - America/Anchorage"],
  ["Pacific/Honolulu", "Hawaii Time - Pacific/Honolulu"],
  ["UTC", "UTC"],
];
const noticeOptions = [[0, "Same day"], [1, "1 day"], [2, "2 days"], [3, "3 days"], [7, "7 days"]];
const bufferOptions = [[0, "None"], [5, "5 minutes"], [10, "10 minutes"], [15, "15 minutes"], [30, "30 minutes"]];
const formatTime = (value) => {
  if (!value) return "";
  const [hour, minute] = value.split(":").map(Number);
  return new Date(2026, 0, 1, hour, minute).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

export default function AvailabilitySettingsCard({ compact = false }) {
  const [browserTimeZone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York");
  const [settings, setSettings] = useState({ timezone: browserTimeZone, minimum_notice_days: 1, booking_window_days: 30, buffer_before_minutes: 0, buffer_after_minutes: 0 });
  const [windows, setWindows] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [rangeDraft, setRangeDraft] = useState({ weekday: 0, start_time: "09:00", end_time: "17:00", is_active: true });
  const [overrideDraft, setOverrideDraft] = useState({ date: "", is_unavailable: true, start_time: "10:00", end_time: "13:00", note: "" });
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    const [settingsRes, windowsRes, overridesRes] = await Promise.all([
      authFetch(`${API_URL}/meetings/settings/`),
      authFetch(`${API_URL}/meetings/weekly-availability/`),
      authFetch(`${API_URL}/meetings/exceptions/`),
    ]);
    if (settingsRes.ok) {
      const data = await settingsRes.json();
      setSettings({ ...data, timezone: data.timezone || browserTimeZone, minimum_notice_days: data.minimum_notice_days ?? Math.round((data.minimum_notice_minutes || 1440) / 1440) });
    }
    if (windowsRes.ok) setWindows((await windowsRes.json()).results || []);
    if (overridesRes.ok) setOverrides((await overridesRes.json()).results || []);
  }, [browserTimeZone]);

  useEffect(() => { load(); }, [load]);

  const groupedWindows = useMemo(() => days.map((day, weekday) => ({
    day,
    weekday,
    ranges: windows.filter((window) => Number(window.weekday) === weekday && window.is_active),
  })), [windows]);

  async function saveSettings(e) {
    e.preventDefault();
    setStatus("");
    const res = await authFetch(`${API_URL}/meetings/settings/`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(settings),
    });
    const data = await res.json();
    if (res.ok) {
      setSettings({ ...data, minimum_notice_days: data.minimum_notice_days ?? settings.minimum_notice_days });
      setStatus("Availability settings saved.");
    } else {
      setStatus(data.detail || "Unable to save settings.");
    }
  }

  async function addRange(e) {
    e.preventDefault();
    const res = await authFetch(`${API_URL}/meetings/weekly-availability/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(rangeDraft),
    });
    if (res.ok) load();
  }

  async function removeRange(id) {
    await authFetch(`${API_URL}/meetings/weekly-availability/${id}/`, { method: "DELETE" });
    load();
  }

  async function markUnavailable(weekday) {
    await Promise.all(windows.filter((window) => Number(window.weekday) === weekday).map((window) => authFetch(`${API_URL}/meetings/weekly-availability/${window.id}/`, { method: "DELETE" })));
    await authFetch(`${API_URL}/meetings/weekly-availability/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ weekday, start_time: "09:00", end_time: "17:00", is_active: false }),
    });
    load();
  }

  async function copyMondayToWeekdays() {
    const mondayRanges = windows.filter((window) => Number(window.weekday) === 0 && window.is_active);
    for (const weekday of [1, 2, 3, 4]) {
      await markUnavailable(weekday);
      for (const range of mondayRanges) {
        await authFetch(`${API_URL}/meetings/weekly-availability/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ weekday, start_time: range.start_time, end_time: range.end_time, is_active: true }),
        });
      }
    }
    load();
  }

  async function addOverride(e) {
    e.preventDefault();
    const payload = { ...overrideDraft };
    if (payload.is_unavailable) {
      payload.start_time = null;
      payload.end_time = null;
    }
    const res = await authFetch(`${API_URL}/meetings/exceptions/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      setShowOverrideForm(false);
      load();
    }
  }

  async function removeOverride(id) {
    await authFetch(`${API_URL}/meetings/exceptions/${id}/`, { method: "DELETE" });
    load();
  }

  return (
    <section className={`availability-settings-content ${compact ? "compact" : ""}`}>
      {status && <p className="meeting-notice">{status}</p>}

      <form className="staff-card availability-preferences" onSubmit={saveSettings}>
        <div className="settings-section-heading">
          <div>
            <h2>Meeting Availability</h2>
            <p>Control when people can request meetings with you.</p>
          </div>
          <button className="btn btn-primary" type="submit">Save Preferences</button>
        </div>
        <div className="preference-grid">
          <label>Time zone<select value={settings.timezone} onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}>{timeZones.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label>Minimum notice<select value={settings.minimum_notice_days} onChange={(e) => setSettings({ ...settings, minimum_notice_days: Number(e.target.value) })}>{noticeOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label>Booking window<select value={settings.booking_window_days} onChange={(e) => setSettings({ ...settings, booking_window_days: Number(e.target.value) })}><option value={14}>14 days</option><option value={30}>30 days</option><option value={60}>60 days</option><option value={90}>90 days</option></select></label>
          <label>Buffer before<select value={settings.buffer_before_minutes} onChange={(e) => setSettings({ ...settings, buffer_before_minutes: Number(e.target.value) })}>{bufferOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label>Buffer after<select value={settings.buffer_after_minutes} onChange={(e) => setSettings({ ...settings, buffer_after_minutes: Number(e.target.value) })}>{bufferOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        </div>
      </form>

      <section className="staff-card weekly-schedule-card">
        <div className="settings-section-heading">
          <div>
            <h2>Weekly Schedule</h2>
            <p>Times are saved in {settings.timezone}.</p>
          </div>
          <button type="button" className="btn btn-secondary" onClick={copyMondayToWeekdays}>Copy Monday to Weekdays</button>
        </div>

        <form className="inline-range-form" onSubmit={addRange}>
          <select value={rangeDraft.weekday} onChange={(e) => setRangeDraft({ ...rangeDraft, weekday: Number(e.target.value) })}>{days.map((day, index) => <option key={day} value={index}>{day}</option>)}</select>
          <input type="time" value={rangeDraft.start_time} onChange={(e) => setRangeDraft({ ...rangeDraft, start_time: e.target.value })} />
          <input type="time" value={rangeDraft.end_time} onChange={(e) => setRangeDraft({ ...rangeDraft, end_time: e.target.value })} />
          <button className="btn btn-primary">Add Time Range</button>
        </form>

        <div className="weekday-list">
          {groupedWindows.map((item) => (
            <article className="weekday-row" key={item.day}>
              <strong>{item.day}</strong>
              <div className="weekday-ranges">
                {item.ranges.length === 0 ? <span className="unavailable-label">Unavailable</span> : item.ranges.map((range) => (
                  <span className="range-pill" key={range.id}>{formatTime(range.start_time)} - {formatTime(range.end_time)}<button type="button" onClick={() => removeRange(range.id)}>Remove</button></span>
                ))}
              </div>
              <button type="button" className="text-button" onClick={() => markUnavailable(item.weekday)}>Mark Unavailable</button>
            </article>
          ))}
        </div>
      </section>

      <section className="staff-card date-overrides-card">
        <div className="settings-section-heading">
          <div>
            <h2>Date Overrides</h2>
            <p>Use these for holidays, travel, or special one-day hours.</p>
          </div>
          <button type="button" className="btn btn-secondary" onClick={() => setShowOverrideForm((value) => !value)}>{showOverrideForm ? "Close" : "Add Date Override"}</button>
        </div>

        {showOverrideForm && <form className="override-form" onSubmit={addOverride}>
          <input type="date" value={overrideDraft.date} onChange={(e) => setOverrideDraft({ ...overrideDraft, date: e.target.value })} />
          <label className="meeting-check"><input type="checkbox" checked={overrideDraft.is_unavailable} onChange={(e) => setOverrideDraft({ ...overrideDraft, is_unavailable: e.target.checked })} /> Unavailable all day</label>
          {!overrideDraft.is_unavailable && <><input type="time" value={overrideDraft.start_time} onChange={(e) => setOverrideDraft({ ...overrideDraft, start_time: e.target.value })} /><input type="time" value={overrideDraft.end_time} onChange={(e) => setOverrideDraft({ ...overrideDraft, end_time: e.target.value })} /></>}
          <input value={overrideDraft.note} onChange={(e) => setOverrideDraft({ ...overrideDraft, note: e.target.value })} placeholder="Optional note" />
          <button className="btn btn-primary">Save Override</button>
        </form>}

        <div className="override-list">
          {overrides.length === 0 ? <p className="compact-empty">No upcoming date overrides.</p> : overrides.map((override) => (
            <div className="override-row" key={override.id}>
              <span>{new Date(`${override.date}T00:00:00`).toLocaleDateString([], { month: "short", day: "numeric" })}</span>
              <strong>{override.is_unavailable ? "Unavailable all day" : `Available ${formatTime(override.start_time)}-${formatTime(override.end_time)}`}</strong>
              {override.note && <small>{override.note}</small>}
              <button type="button" onClick={() => removeOverride(override.id)}>Remove</button>
            </div>
          ))}
        </div>
      </section>
    </section>
  );
}
